import { FormEvent, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getCurrentStore, updateCurrentStore } from '@/lib/api/stores.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';

const ROLE_OPTIONS = [
  { id: 'tenant_admin', label: 'Tenant Admin' },
  { id: 'store_manager', label: 'Store Manager' },
  { id: 'technician', label: 'Technician' },
  { id: 'front_desk', label: 'Front Desk' },
  { id: 'read_only', label: 'Read Only' },
] as const;

export function EstimatedProfitVisibilitySettings() {
  const queryClient = useQueryClient();
  const storeQuery = useQuery({ queryKey: ['store-current'], queryFn: getCurrentStore });
  const [roles, setRoles] = useState<string[]>(['tenant_admin', 'store_manager']);

  useEffect(() => {
    const fromStore = storeQuery.data?.showEstimatedProfitRoles;
    if (Array.isArray(fromStore)) setRoles(fromStore);
  }, [storeQuery.data?.showEstimatedProfitRoles]);

  const mutation = useMutation({
    mutationFn: () => updateCurrentStore({ showEstimatedProfitRoles: roles }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['store-current'] });
      toastSuccess('Estimated profit visibility saved');
    },
    onError: (error) => {
      const parsed = parseApiError(error);
      toastError(parsed.message, parsed.code);
    },
  });

  function toggleRole(roleId: string) {
    setRoles((current) =>
      current.includes(roleId) ? current.filter((r) => r !== roleId) : [...current, roleId],
    );
  }

  return (
    <form
      className="mb-6 space-y-4 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
      onSubmit={(event: FormEvent) => {
        event.preventDefault();
        mutation.mutate();
      }}
    >
      <div>
        <h3 className="text-lg font-semibold text-slate-950">Estimated Profit Visibility</h3>
        <p className="mt-1 text-sm text-slate-500">
          Choose which roles can see estimated profit on ticket detail pages.
        </p>
      </div>
      <div className="space-y-2">
        {ROLE_OPTIONS.map((role) => (
          <label
            key={role.id}
            className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3 text-sm font-medium"
          >
            <span>{role.label}</span>
            <input
              type="checkbox"
              checked={roles.includes(role.id)}
              onChange={() => toggleRole(role.id)}
            />
          </label>
        ))}
      </div>
      <button
        type="submit"
        className="rounded-xl bg-primary px-4 py-2 font-semibold text-white disabled:opacity-50"
        disabled={mutation.isPending || storeQuery.isLoading}
      >
        Save
      </button>
    </form>
  );
}
